import { Matrix4, Vector2, Vector3 } from 'three';

import { FatLine, LineGeometry, LineMaterial } from '@/lib/three';
import { ILineOptions, ILineUserData, WidgetObject } from '@/types';

import { Line3d } from './Line3d';

export class Line2d {
  static POINT_SIZE = 0.1;
  static GROW_POINTS = 16;

  static DIM = 2;

  /**
   * 创建 2D 折线
   * @param data [x1, y1 x2, y2, ...] three.js 坐标系
   * @param options 配置项
   * @returns
   */
  static create(data: WidgetObject['data'], options: ILineOptions): FatLine {
    const { color, dashConfig, maxPoints, growable, canvasSize = [1000, 1000] } = options;

    const pointsLen = Math.floor(data.length / Line2d.DIM);
    const growLen = maxPoints && maxPoints > pointsLen ? maxPoints - pointsLen : growable ? Line2d.GROW_POINTS : 0;
    const allPoints = pointsLen + growLen;

    const positions = new Float32Array(allPoints * 3).fill(0);

    for (let i = 0; i < pointsLen; i++) {
      positions[i * 3] = data[i * Line2d.DIM];
      positions[i * 3 + 1] = data[i * Line2d.DIM + 1];
      positions[i * 3 + 2] = 0;
    }

    const geometry = new LineGeometry();

    geometry.instanceCount = pointsLen - 1;
    geometry.setPositions(positions);

    const matConfig = {
      color,
      linewidth: 1,
      resolution: new Vector2(...canvasSize),
      alphaToCoverage: true,
      dashed: false,
      dashSize: 1,
      gapSize: 1,
    };

    if (dashConfig) {
      matConfig.dashed = true;
      matConfig.dashSize = dashConfig[0];
      matConfig.gapSize = dashConfig[1];
    }

    const line = new FatLine(geometry, new LineMaterial(matConfig));

    line.name = line.uuid;
    line.userData = { type: 'line2d', ...options } satisfies ILineUserData;

    if (dashConfig) {
      line.computeLineDistances();
    }

    if ((line.userData as ILineUserData).withDot) {
      const dots = Line3d.createPoints(positions, color, line.layers.mask);
      line.add(dots);
    }
    return line;
  }

  /**
   * 更新 2D 折线
   * @param target
   * @param data
   */
  static update(target: FatLine, data: WidgetObject['data']) {
    Line3d.update(target, Line2d.patchZeros(data));
  }

  /**
   * 根据 widget 生成折线
   * @param name
   * @param widget
   * @param matrix
   * @param options
   */
  static createFromWidget(name: string, widget: WidgetObject, matrix: Matrix4, options: ILineOptions): FatLine {
    const data = Line2d.pointsApplyMatrix4(widget.data, matrix);

    const line = Line2d.create(data, options);
    line.name = name;

    return line;
  }

  static updateFromWidget(target: FatLine, widget: WidgetObject, matrix: Matrix4) {
    Line2d.update(target, Line2d.pointsApplyMatrix4(widget.data, matrix));
  }

  /**
   * 导出Widget格式数据
   * @param target
   * @param matrix 从 threejs 到文件坐标系
   */
  static transOutWidget(target: FatLine, matrix: Matrix4): WidgetObject {
    const data = Line2d.pointsApplyMatrix4(Line2d.getVertexFromObject3D(target), matrix);

    return {
      name: 'line2d',
      data,
      line_type: 'line',
    };
  }

  static changeColor(target: FatLine, colorHex: string, refreshData = true) {
    Line3d.changeColor(target, colorHex, refreshData);
  }

  static pointsApplyMatrix4(data: number[] | ArrayLike<number>, matrix: Matrix4) {
    const vec = new Vector3();
    const len = Math.floor(data.length / Line2d.DIM) * Line2d.DIM;

    const newData: number[] = new Array(len);
    for (let i = 0; i + 1 < len; i += Line2d.DIM) {
      vec.set(data[i], data[i + 1], 0).applyMatrix4(matrix);
      newData[i] = vec.x;
      newData[i + 1] = vec.y;
    }

    return newData;
  }

  static getVertexFromObject3D(target: FatLine): Float32Array {
    const positionAttr = target.geometry.attributes.position;
    const pointLen = Math.min(target.geometry.drawRange.count, positionAttr.count);

    const positions = new Float32Array(pointLen * Line2d.DIM);

    for (let i = 0; i < pointLen; i++) {
      positions[i * Line2d.DIM] = positionAttr.getX(i);
      positions[i * Line2d.DIM + 1] = positionAttr.getY(i);
    }

    return positions;
  }

  /**
   * 将 [x1, y1 x2, y2, ...] 转换为 [x1, y1, 0,  x2, y2, 0, ...]
   * @param data
   */
  static patchZeros(data: number[]) {
    const pointsLen = Math.floor(data.length / Line2d.DIM);

    const positions = new Array(pointsLen * 3).fill(0);
    for (let i = 0; i < pointsLen; i++) {
      positions[i * 3] = data[i * Line2d.DIM];
      positions[i * 3 + 1] = data[i * Line2d.DIM + 1];
      positions[i * 3 + 2] = 0;
    }

    return positions;
  }
}
